import React, { CSSProperties, useMemo } from 'react';
import { z } from 'zod';

import { Img, ImgProps as BaseImgProps } from '@react-email/img';
import { Link } from '@react-email/link';
import { useRenderLiquidTemplate } from '@wp-block-builder/stores/WaypointRendererStore';

import defineBlock from './helpers/defineBlock';

export const ImageBlockSchema = defineBlock(
  'Image',
  z.object({
    imageType: z.enum(['UPLOAD', 'URL_TEMPLATE']).nullish(),
    url: z.string().nullish(),
    urlTemplate: z.string().nullish(),
    uploadId: z.string().nullish(),
    alt: z.string().nullish(),
    contentAlignment: z.enum(['top', 'middle', 'bottom']).nullish(),
    linkHref: z.string().nullish(),
    height: z.number().nullish(),
    width: z.number().nullish(),
  })
);
export type TImageBlock = z.infer<typeof ImageBlockSchema>;

function useStyle(block: TImageBlock) {
  const verticalAlign = block.props?.contentAlignment ?? 'middle';
  const height = block.props?.height ?? undefined;
  const width = block.props?.width ?? undefined;

  return useMemo(() => {
    const value: CSSProperties = {
      verticalAlign,
      display: 'inline-block',
      maxWidth: '100%',
    };
    if (typeof height === 'number') {
      value.height = `${height}px`;
    }
    if (typeof width === 'number') {
      value.width = `${width}px`;
    }
    return value;
  }, [verticalAlign, height, width]);
}

type ImgProps = {
  width: BaseImgProps['width'];
  height: BaseImgProps['height'];
  src: BaseImgProps['src'];
  style: BaseImgProps['style'];
  alt?: BaseImgProps['alt'];
};

type ImageBlockProps = {
  block: TImageBlock;
  placeholder?: JSX.Element;
  skipLiquid: boolean;
};
export default function ImageBlock({ block, placeholder, skipLiquid }: ImageBlockProps) {
  const linkHref = useRenderLiquidTemplate({ template: block.props?.linkHref ?? '', skipLiquid });
  const alt = useRenderLiquidTemplate({ template: block.props?.alt ?? '', skipLiquid });

  const urlTemplate = useRenderLiquidTemplate({ template: block.props?.urlTemplate ?? '', skipLiquid });
  const imageType = block.props?.imageType;
  const getImageUrl = () => {
    switch (imageType) {
      case 'URL_TEMPLATE':
        return urlTemplate;
      default:
        return block?.props?.url ?? '';
    }
  };
  const url = getImageUrl();

  const style = useStyle(block);

  const height = block.props?.height ?? undefined;
  const width = block.props?.width ?? undefined;

  return useMemo(() => {
    if (typeof url !== 'string' || url.trim().length === 0) {
      return placeholder ?? null;
    }

    const imgProps: ImgProps = {
      width,
      height,
      src: url,
      style,
    };
    if (alt.trim().length > 0) {
      imgProps.alt = alt;
    }

    const children = <Img {...imgProps} />;
    return linkHref.trim().length === 0 ? children : <Link href={linkHref}>{children}</Link>;
  }, [placeholder, url, alt, linkHref, style, height, width]);
}
